import { useEffect, useState } from 'react';
import { useIsCallerTemporaryAdmin } from '../hooks/useQueries';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Clock, AlertTriangle } from 'lucide-react';

export default function TemporaryAdminBanner() {
    const { data: isTemporaryAdmin } = useIsCallerTemporaryAdmin();
    const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number | null>(null);

    // Session timer effect
    useEffect(() => {
        if (!isTemporaryAdmin) {
            setSessionTimeRemaining(null);
            return;
        }

        // Get session start time from localStorage
        const sessionStartStr = localStorage.getItem('tempAdminSessionStart');
        if (!sessionStartStr) {
            setSessionTimeRemaining(null);
            return;
        }

        const sessionStart = parseInt(sessionStartStr, 10);
        const sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds

        const updateTimer = () => {
            const now = Date.now();
            const elapsed = now - sessionStart;
            const remaining = sessionTimeout - elapsed;

            if (remaining <= 0) {
                setSessionTimeRemaining(0);
            } else {
                setSessionTimeRemaining(Math.floor(remaining / 1000 / 60)); // Convert to minutes
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 30000); // Update every 30 seconds (reduced frequency to lower CPU)

        return () => clearInterval(interval);
    }, [isTemporaryAdmin]);

    if (!isTemporaryAdmin) {
        return null;
    }

    const isExpiringSoon = sessionTimeRemaining !== null && sessionTimeRemaining <= 2;

    return (
        <div className={`border-b ${
            isExpiringSoon 
                ? 'bg-gradient-to-r from-yellow-500/20 via-yellow-500/10 to-yellow-500/20 border-yellow-500/30' 
                : 'bg-gradient-to-r from-yellow-500/20 via-yellow-500/10 to-yellow-500/20 border-yellow-500/30'
        }`}>
            <div className="container mx-auto px-4 py-2">
                <div className="flex items-center justify-center gap-3 flex-wrap">
                    {isExpiringSoon ? (
                        <AlertTriangle className="h-4 w-4 text-yellow-500 animate-pulse" />
                    ) : (
                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className="text-sm font-medium text-yellow-500">
                        Temporary Admin Mode Active
                    </span>
                    {sessionTimeRemaining !== null && (
                        <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3 text-yellow-500" />
                            <span className={`text-xs ${isExpiringSoon ? 'font-bold animate-pulse' : 'font-medium'} text-yellow-500`}>
                                {sessionTimeRemaining} min remaining
                            </span>
                        </div>
                    )}
                    <Badge 
                        variant="outline" 
                        className={`border-yellow-500/50 text-yellow-500 ${isExpiringSoon ? 'animate-pulse' : ''}`}
                    >
                        {isExpiringSoon ? 'Expiring Soon' : 'Session Active'}
                    </Badge>
                </div>
            </div>
        </div>
    );
}
